import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, toast } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Users, Plus, Trash2, Edit, Shield, Eye, EyeOff, User } from 'lucide-react';
import AuthorSettings from '../components/AuthorSettings';

const GRADES = [
  { value: 'amiral', label: 'Amiral', color: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  { value: 'capitaine', label: 'Capitaine', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
  { value: 'lieutenant', label: 'Lieutenant', color: 'bg-sky-500/15 text-sky-400 border-sky-500/30' },
];

const GradeBadge = ({ grade }) => {
  const g = GRADES.find(x => x.value === grade) || GRADES[2];
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded font-mono text-xs uppercase tracking-wider border ${g.color}`}
      data-testid={`grade-badge-${grade}`}>
      <Shield className="w-3 h-3" />{g.label}
    </span>
  );
};

const emptyForm = { email: '', password: '', full_name: '', grade: 'lieutenant' };

export default function Equipage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [addForm, setAddForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState(emptyForm);
  const [showAddPw, setShowAddPw] = useState(false);
  const [showEditPw, setShowEditPw] = useState(false);
  const [authorSettingsUser, setAuthorSettingsUser] = useState(null);  // User dont on modifie les auteurs

  const loadUsers = useCallback(async () => {
    try {
      setUsers((await axios.get(`${API}/users`)).data);
    } catch (e) { toast.error('Erreur chargement des comptes'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAdd = async () => {
    if (!addForm.email || !addForm.password) { toast.error('Email et mot de passe requis'); return; }
    try {
      await axios.post(`${API}/auth/register`, addForm);
      toast.success(`Compte "${addForm.email}" cree`);
      setAddForm(emptyForm);
      setShowAdd(false);
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur creation'); }
  };

  const openEdit = (user) => {
    setEditForm({ email: user.email, full_name: user.full_name || '', grade: user.grade || 'lieutenant', password: '' });
    setEditingUser(user);
    setShowEditPw(false);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;
    try {
      const payload = { email: editForm.email, full_name: editForm.full_name, grade: editForm.grade };
      if (editForm.password) payload.password = editForm.password;
      await axios.put(`${API}/users/${editingUser.id}`, payload);
      toast.success('Compte mis a jour');
      setEditingUser(null);
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur mise a jour'); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/users/${confirmDelete.id}`);
      toast.success(`Compte "${confirmDelete.email}" supprime`);
      setConfirmDelete(null);
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleGradeChange = async (userId, newGrade) => {
    try {
      await axios.put(`${API}/users/${userId}`, { grade: newGrade });
      toast.success('Grade mis a jour');
      await loadUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur changement de grade'); }
  };

  const GradeSelector = ({ value, onChange, testIdPrefix }) => (
    <div className="flex gap-2">
      {GRADES.map(g => (
        <button key={g.value} type="button" onClick={() => onChange(g.value)}
          className={`text-xs font-mono px-3 py-1.5 rounded border transition-all ${value === g.value ? g.color : 'bg-slate-900/50 border-slate-700 text-slate-500 hover:border-slate-500'}`}
          data-testid={`${testIdPrefix}-grade-${g.value}`}>
          {g.label}
        </button>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen p-6" data-testid="equipage-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-wider uppercase"
            style={{ fontFamily: "'Orbitron', monospace", color: '#00e5ff', textShadow: '0 0 20px rgba(0,229,255,0.3)' }}>
            EQUIPAGE
          </h1>
          <p className="text-slate-500 font-mono text-sm">{users.length} membre{users.length > 1 ? 's' : ''} d'equipage</p>
        </div>
        <Button onClick={() => { setAddForm(emptyForm); setShowAdd(true); setShowAddPw(false); }}
          className="bg-sky-600 hover:bg-sky-500 text-white font-semibold" data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />Nouveau membre
        </Button>
      </div>

      {loading ? (
        <p className="text-slate-500 font-mono text-center py-12">Chargement...</p>
      ) : users.length === 0 ? (
        <Card className="bg-slate-900/30 border-slate-800">
          <CardContent className="py-12 text-center">
            <Users className="w-10 h-10 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 font-mono text-sm">Aucun membre d'equipage</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.id} className="bg-slate-900/30 border-slate-800 hover:border-slate-600 transition-all" data-testid={`user-card-${u.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-sky-400 font-mono text-sm font-bold">
                        {(u.full_name || u.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-slate-200 font-medium text-sm truncate">{u.full_name || 'Sans nom'}</p>
                      <p className="text-slate-500 font-mono text-xs truncate">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <select value={u.grade || 'lieutenant'}
                      onChange={(e) => handleGradeChange(u.id, e.target.value)}
                      className="bg-slate-900/80 border border-slate-700 text-slate-300 rounded px-2 py-1 text-xs font-mono focus:border-sky-500 focus:outline-none cursor-pointer"
                      data-testid={`grade-select-${u.id}`}>
                      {GRADES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
                    </select>
                    <GradeBadge grade={u.grade || 'lieutenant'} />
                    <button onClick={() => setAuthorSettingsUser(u)} 
                      className="p-1.5 hover:bg-slate-700 rounded transition-colors" 
                      title="Gérer les noms d'auteur"
                      data-testid={`author-settings-${u.id}`}>
                      <User className="w-4 h-4 text-slate-400 hover:text-purple-400" />
                    </button>
                    <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-slate-700 rounded transition-colors" data-testid={`edit-user-${u.id}`}>
                      <Edit className="w-4 h-4 text-slate-400 hover:text-sky-400" />
                    </button>
                    <button onClick={() => setConfirmDelete(u)} className="p-1.5 hover:bg-red-900/30 rounded transition-colors" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog: Ajouter */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>Nouveau membre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Nom complet</label>
              <Input value={addForm.full_name} onChange={(e) => setAddForm(p => ({ ...p, full_name: e.target.value }))}
                placeholder="Jean Dupont" className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
                data-testid="add-user-name" />
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Email (login)</label>
              <Input type="email" value={addForm.email} onChange={(e) => setAddForm(p => ({ ...p, email: e.target.value }))}
                placeholder="email@exemple.com" className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200"
                data-testid="add-user-email" />
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Mot de passe</label>
              <div className="mt-1 relative">
                <Input type={showAddPw ? 'text' : 'password'} value={addForm.password}
                  onChange={(e) => setAddForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Mot de passe" className="bg-slate-900/50 border-slate-700 text-slate-200 pr-10"
                  data-testid="add-user-password" />
                <button type="button" onClick={() => setShowAddPw(!showAddPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showAddPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Grade</label>
              <div className="mt-2">
                <GradeSelector value={addForm.grade} onChange={(v) => setAddForm(p => ({ ...p, grade: v }))} testIdPrefix="add" />
              </div>
            </div>
            <Button onClick={handleAdd} className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold mt-2" data-testid="submit-add-user">
              <Plus className="w-4 h-4 mr-2" />Creer le compte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Modifier */}
      <Dialog open={!!editingUser} onOpenChange={(open) => { if (!open) setEditingUser(null); }}>
        <DialogContent className="bg-slate-950 border-sky-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-sky-400" style={{ fontFamily: "'Orbitron', monospace" }}>Modifier le compte</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Nom complet</label>
              <Input value={editForm.full_name} onChange={(e) => setEditForm(p => ({ ...p, full_name: e.target.value }))}
                className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200" data-testid="edit-user-name" />
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Email (login)</label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm(p => ({ ...p, email: e.target.value }))}
                className="mt-1 bg-slate-900/50 border-slate-700 text-slate-200" data-testid="edit-user-email" />
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Nouveau mot de passe <span className="text-slate-600 normal-case">(laisser vide pour ne pas changer)</span></label>
              <div className="mt-1 relative">
                <Input type={showEditPw ? 'text' : 'password'} value={editForm.password}
                  onChange={(e) => setEditForm(p => ({ ...p, password: e.target.value }))}
                  placeholder="Nouveau mot de passe" className="bg-slate-900/50 border-slate-700 text-slate-200 pr-10"
                  data-testid="edit-user-password" />
                <button type="button" onClick={() => setShowEditPw(!showEditPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                  {showEditPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 uppercase tracking-wider">Grade</label>
              <div className="mt-2">
                <GradeSelector value={editForm.grade} onChange={(v) => setEditForm(p => ({ ...p, grade: v }))} testIdPrefix="edit" />
              </div>
            </div>
            <Button onClick={handleSaveEdit} className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold mt-2" data-testid="submit-edit-user">
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Confirmer suppression */}
      <Dialog open={!!confirmDelete} onOpenChange={(open) => { if (!open) setConfirmDelete(null); }}>
        <DialogContent className="bg-slate-950 border-red-500/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-400">Supprimer le compte</DialogTitle>
          </DialogHeader>
          <p className="text-slate-300 text-sm mt-2">
            Supprimer definitivement le compte de <strong className="text-white">{confirmDelete?.full_name || confirmDelete?.email}</strong> ?
          </p>
          <p className="text-slate-500 text-xs mt-1">Cette action est irreversible.</p>
          <div className="flex gap-3 mt-4 justify-end">
            <Button variant="outline" onClick={() => setConfirmDelete(null)} className="border-slate-600 text-slate-300" data-testid="cancel-delete-user">Annuler</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-user">
              <Trash2 className="w-4 h-4 mr-2" />Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Paramètres auteur d'un membre */}
      <Dialog open={!!authorSettingsUser} onOpenChange={(open) => { if (!open) setAuthorSettingsUser(null); }}>
        <DialogContent className="bg-slate-950 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-sky-400 flex items-center gap-2">
              <User className="w-5 h-5" />
              Noms d'auteur de {authorSettingsUser?.full_name || authorSettingsUser?.email}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {authorSettingsUser && (
              <AuthorSettings 
                userId={authorSettingsUser.id} 
                isAdmin={true}
                onUpdate={() => {}}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
